/**
 * useLinkRecordRows — shared state for the "Link record" picker rows used by
 * both the note composer (`useMemoCreate`) and the note slideout
 * (`NoteLinkedRecordsSection`).
 *
 * Owns the array of in-progress `LinkedAssociation` rows plus the add / remove /
 * association-type / target handlers. This is the logic both surfaces used to
 * duplicate verbatim.
 *
 * Commit model is decided by the caller via `onRowComplete`:
 *   - **Composer (deferred):** omit `onRowComplete`. Rows accumulate and the
 *     caller reads `rows` at note-create time to build association events.
 *   - **Slideout (immediate):** pass `onRowComplete`. It fires the moment a row
 *     has both an association type and ≥1 target, letting the caller persist the
 *     link immediately (and typically remove the row afterwards).
 */

import { useCallback, useRef, useState } from 'react';

import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import type { LinkedAssociation } from './LinkPickerRow';

export interface UseLinkRecordRowsResult {
  rows: LinkedAssociation[];
  addRow: () => void;
  removeRow: (id: string) => void;
  setAssociationType: (id: string, associationTypeName: string) => void;
  setTarget: (id: string, value: string[] | null) => void;
  reset: () => void;
}

interface UseLinkRecordRowsOptions {
  /**
   * Fired when a row becomes complete (association type + ≥1 target). Use for
   * immediate-commit surfaces. The provided row carries the just-selected
   * targets. Omit for deferred-commit surfaces.
   */
  onRowComplete?: (row: LinkedAssociation) => void;
}

export function useLinkRecordRows(
  linkableAssocTypes: AssociationType[],
  options?: UseLinkRecordRowsOptions
): UseLinkRecordRowsResult {
  const [rows, setRows] = useState<LinkedAssociation[]>([]);

  // Keep a ref to the latest rows so `setTarget` can read a row's current
  // association type when deciding whether the row just became complete,
  // without adding `rows` to its dependency list.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const onRowComplete = options?.onRowComplete;

  const addRow = useCallback(() => {
    setRows(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        associationType: null,
        targetEntityIds: [],
      },
    ]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  }, []);

  const setAssociationType = useCallback(
    (id: string, associationTypeName: string) => {
      setRows(prev =>
        prev.map(r => {
          if (r.id !== id) {
            return r;
          }
          const at = linkableAssocTypes.find(
            t => t.association_type_name === associationTypeName
          );
          // Picking a new association type invalidates any previously chosen
          // targets (their entity types likely no longer match).
          return { ...r, associationType: at ?? null, targetEntityIds: [] };
        })
      );
    },
    [linkableAssocTypes]
  );

  const setTarget = useCallback(
    (id: string, value: string[] | null) => {
      setRows(prev =>
        prev.map(r =>
          r.id === id ? { ...r, targetEntityIds: value ?? [] } : r
        )
      );
      if (onRowComplete && value && value.length > 0) {
        const existing = rowsRef.current.find(r => r.id === id);
        if (existing?.associationType) {
          onRowComplete({ ...existing, targetEntityIds: value });
        }
      }
    },
    [onRowComplete]
  );

  const reset = useCallback(() => setRows([]), []);

  return { rows, addRow, removeRow, setAssociationType, setTarget, reset };
}
